import { useState, useEffect } from 'react';
import { Loader2, Plus, Pencil, Trash2, Check, AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/api/apiClient';

/**
 * The payment methods list (SellGo's own database): the manager ticks the
 * methods the reps collect with (Saudi ones first, then Egyptian ones) and can
 * add the company's own. Cash and bank transfer are in both lists: one switch.
 * In Settings → وسائل الدفع, and in the first-time setup.
 */

// The kind decides what the collection screen asks for (transfer number, cheque details…).
const KINDS = [
  { key: 'cash', label: 'نقدي' },
  { key: 'card', label: 'شبكة / بطاقة' },
  { key: 'bank', label: 'تحويل بنكي' },
  { key: 'wallet', label: 'محفظة إلكترونية' },
  { key: 'check', label: 'شيك' },
  { key: 'other', label: 'أخرى' },
];

const GROUPS = [
  { country: 'SA', title: 'وسائل الدفع في السعودية' },
  { country: 'EG', title: 'وسائل الدفع في مصر' },
  { country: '', title: 'وسائل أضفتها' },
];

const EMPTY_FORM = { name: '', kind: 'other' };

function MethodRow({ m, busy, onToggle, onEdit, onDelete }) {
  return (
    <div className="flex items-center">
      <button type="button" role="checkbox" aria-checked={m.active} disabled={busy}
        onClick={() => onToggle(m, !m.active)}
        className="flex-1 min-w-0 flex items-center gap-3 px-4 py-3 text-right hover:bg-muted/40 transition-colors disabled:opacity-60">
        <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${m.active ? 'bg-primary border-primary' : 'border-muted-foreground/40'}`}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" /> : m.active && <Check className="w-3.5 h-3.5 text-white" />}
        </span>
        <span className="text-xl w-7 text-center flex-shrink-0">{m.icon}</span>
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-semibold text-foreground">{m.name}</span>
            {m.popular && (
              <span className="text-[10px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">الأكثر استخداماً</span>
            )}
          </span>
          <span className="block text-xs text-muted-foreground">{m.hint}</span>
        </span>
      </button>
      {m.custom && (
        <div className="flex items-center gap-1 pl-3">
          <button type="button" onClick={() => onEdit(m)} title="تعديل"
            className="p-2 rounded-lg text-muted-foreground hover:bg-muted">
            <Pencil className="w-4 h-4" />
          </button>
          {!m.used && (
            <button type="button" onClick={() => onDelete(m)} title="حذف"
              className="p-2 rounded-lg text-red-500 hover:bg-red-50">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function PaymentMethodsEditor() {
  const [methods, setMethods] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);          // the row (or the form) being saved
  const [formFor, setFormFor] = useState(null);    // null | 'new' | id of the method being edited
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleting, setDeleting] = useState(null);  // method waiting for "حذف نهائي"

  const load = async () => {
    setError(null);
    try {
      const res = await api.get('/sellgo_payment_methods_settings');
      setMethods(res?.data || []);
    } catch (e) {
      setError(e?.message || 'تعذّر تحميل وسائل الدفع');
    }
  };

  useEffect(() => { load(); }, []);

  const keyOf = (m) => (m.custom ? `id:${m.id}` : `code:${m.code}`);

  const toggle = async (m, active) => {
    setBusy(keyOf(m));
    try {
      const res = await api.post('/sellgo_payment_method_toggle', m.custom ? { id: m.id, active } : { code: m.code, active });
      const saved = res.data;
      // A method the company had added under the same name is now this one: re-read the list.
      if (!m.custom && methods.some(x => x.custom && x.id === saved.id)) {
        await load();
      } else {
        setMethods(list => list.map(x => (m.custom ? x.custom && x.id === m.id : !x.custom && x.code === m.code)
          ? { ...x, id: saved.id, active: saved.active } : x));
      }
    } catch (e) {
      toast.error(e?.message || 'تعذّر الحفظ');
    }
    setBusy(null);
  };

  const openForm = (m) => {
    setDeleting(null);
    setFormFor(m ? m.id : 'new');
    setForm(m ? { name: m.name, kind: m.kind } : EMPTY_FORM);
  };

  const closeForm = () => { setFormFor(null); setForm(EMPTY_FORM); };

  const save = async () => {
    const name = form.name.trim();
    if (!name) { toast.error('اكتب اسم وسيلة الدفع'); return; }
    setBusy('form');
    try {
      const editing = formFor !== 'new';
      await api.post('/sellgo_payment_method_save', editing ? { id: formFor, name, kind: form.kind } : { name, kind: form.kind });
      toast.success(editing ? 'تم الحفظ' : `تمت إضافة «${name}» وهتظهر في التحصيل`);
      closeForm();
      await load();
    } catch (e) {
      toast.error(e?.message || 'تعذّر الحفظ');
    }
    setBusy(null);
  };

  const remove = async (m) => {
    setBusy(keyOf(m));
    try {
      await api.del(`/sellgo_payment_methods/${m.id}`);
      toast.success(`تم حذف «${m.name}»`);
      setDeleting(null);
      await load();
    } catch (e) {
      toast.error(e?.message || 'تعذّر الحذف');
    }
    setBusy(null);
  };

  const activeCount = methods ? new Set(methods.filter(m => m.active).map(m => m.id)).size : 0;

  const methodForm = (
    <div className="p-4 space-y-3 bg-primary/[0.03]">
      <div>
        <label className="text-xs font-semibold text-muted-foreground">اسم وسيلة الدفع</label>
        <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          placeholder="مثال: محفظة الشركة" className="mt-1 rounded-xl" autoFocus />
      </div>
      <div>
        <label className="text-xs font-semibold text-muted-foreground">النوع</label>
        <div className="flex flex-wrap gap-1.5 mt-1">
          {KINDS.map(k => (
            <button key={k.key} type="button" onClick={() => setForm(f => ({ ...f, kind: k.key }))}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-all ${form.kind === k.key ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>
              {k.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-1.5">النوع بيحدد المندوب هيكتب إيه وقت التحصيل (رقم التحويل، بيانات الشيك...).</p>
      </div>
      <div className="flex gap-2">
        <Button onClick={save} disabled={busy === 'form'} className="flex-1 bg-primary gap-1.5">
          {busy === 'form' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          {formFor === 'new' ? 'إضافة' : 'حفظ'}
        </Button>
        <Button variant="outline" onClick={closeForm} disabled={busy === 'form'} className="flex-1">إلغاء</Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="bg-primary/5 border border-primary/15 rounded-2xl p-3 text-sm text-foreground">
        علّم على الوسائل اللي عايزها تظهر للمندوبين في شاشة التحصيل.
        {methods && <span className="block text-xs text-muted-foreground mt-1">ظاهر الآن في التحصيل: {activeCount.toLocaleString('ar-EG')}</span>}
      </div>

      {!methods && !error && (
        <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>
      )}

      {error && (
        <div className="flex flex-col items-center gap-3 py-10 text-center">
          <AlertCircle className="w-10 h-10 text-red-500" />
          <p className="text-sm font-semibold text-foreground">{error}</p>
          <Button variant="outline" size="sm" onClick={load}>إعادة المحاولة</Button>
        </div>
      )}

      {methods && GROUPS.map(g => {
        const rows = methods.filter(m => m.country === g.country);
        const own = g.country === '';
        if (!rows.length && !own) return null;
        return (
          <div key={g.country || 'own'} className="space-y-1.5">
            <p className="text-xs font-bold text-muted-foreground px-1">{g.title}</p>
            <div className="bg-card border border-border rounded-2xl overflow-hidden divide-y divide-border">
              {rows.map(m => (
                formFor === m.id ? <div key={keyOf(m)}>{methodForm}</div> : (
                  <div key={`${g.country}-${keyOf(m)}`}>
                    <MethodRow m={m} busy={busy === keyOf(m)} onToggle={toggle} onEdit={openForm}
                      onDelete={x => { setFormFor(null); setDeleting(x.id); }} />
                    {deleting === m.id && (
                      <div className="flex items-center gap-2 px-4 pb-3">
                        <span className="flex-1 text-xs text-red-600">حذف «{m.name}» نهائياً؟</span>
                        <Button size="sm" variant="destructive" onClick={() => remove(m)} disabled={busy === keyOf(m)}>حذف نهائي</Button>
                        <Button size="sm" variant="outline" onClick={() => setDeleting(null)}>إلغاء</Button>
                      </div>
                    )}
                  </div>
                )
              ))}
              {own && (formFor === 'new' ? methodForm : (
                <button type="button" onClick={() => openForm(null)}
                  className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold text-primary hover:bg-primary/5">
                  <Plus className="w-4 h-4" /> إضافة وسيلة دفع أخرى
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
